/**
 * WhatsApp-style message link detection and formatting utilities
 */

export interface LinkMatch {
  type: 'phone' | 'email' | 'time';
  text: string;
  start: number;
  end: number;
  href?: string;
}

/**
 * Regex patterns for detecting different types of links in messages
 */
const PATTERNS = {
  // Phone numbers: +56 9 9419 6545, +1-555-123-4567, etc.
  phone: /(\+\d{1,3}[\s-]?\d{1,4}[\s-]?\d{3,4}[\s-]?\d{3,4})/g,

  // Email addresses: user@domain.com
  email: /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,

  // Time expressions: hoy a las 12:00, today at 12:00, etc.
  time: /(hoy a las \d{1,2}:\d{2}|today at \d{1,2}:\d{2}|mañana a las \d{1,2}:\d{2}|tomorrow at \d{1,2}:\d{2}|ayer a las \d{1,2}:\d{2}|yesterday at \d{1,2}:\d{2})/gi
};

/**
 * Parse message content and extract all links
 */
export function parseMessageLinks(content: string): LinkMatch[] {
  const links: LinkMatch[] = [];

  // Find phone numbers
  let match;
  while ((match = PATTERNS.phone.exec(content)) !== null) {
    links.push({
      type: 'phone',
      text: match[1],
      start: match.index,
      end: match.index + match[1].length,
      href: `tel:${match[1].replace(/[\s-]/g, '')}`
    });
  }

  // Reset regex lastIndex
  PATTERNS.phone.lastIndex = 0;

  // Find email addresses
  while ((match = PATTERNS.email.exec(content)) !== null) {
    links.push({
      type: 'email',
      text: match[1],
      start: match.index,
      end: match.index + match[1].length,
      href: `mailto:${match[1]}`
    });
  }

  // Reset regex lastIndex
  PATTERNS.email.lastIndex = 0;

  // Find time expressions
  while ((match = PATTERNS.time.exec(content)) !== null) {
    links.push({
      type: 'time',
      text: match[1],
      start: match.index,
      end: match.index + match[1].length
    });
  }

  // Reset regex lastIndex
  PATTERNS.time.lastIndex = 0;

  // Sort by start position
  return links.sort((a, b) => a.start - b.start);
}

/**
 * Convert message content into array of text segments and links
 */
export function segmentMessageContent(content: string): Array<{ type: 'text' | 'link'; content: string; linkData?: LinkMatch }> {
  const links = parseMessageLinks(content);
  const segments: Array<{ type: 'text' | 'link'; content: string; linkData?: LinkMatch }> = [];

  let lastIndex = 0;

  links.forEach(link => {
    // Add text before link
    if (link.start > lastIndex) {
      segments.push({
        type: 'text',
        content: content.substring(lastIndex, link.start)
      });
    }

    // Add link
    segments.push({
      type: 'link',
      content: link.text,
      linkData: link
    });

    lastIndex = link.end;
  });

  // Add remaining text
  if (lastIndex < content.length) {
    segments.push({
      type: 'text',
      content: content.substring(lastIndex)
    });
  }

  return segments;
}
